import { useRef } from 'react'
import type { MathLayout, MathProblem } from '../../types'
import { MATH_OPERATION_SYMBOLS } from '../../types'
import { correctAnswer } from '../../lib/math/generator'

// ─── Types ────────────────────────────────────────────────────────────────────

interface MathProblemSetProps {
  problems:  MathProblem[]
  answers:   string[]
  checked:   boolean
  layout:    MathLayout
  onAnswer:  (idx: number, value: string) => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function checkStatus(p: MathProblem, answer: string): 'correct' | 'wrong' | 'empty' {
  if (answer === '' || answer === undefined) return 'empty'
  const n = parseInt(answer, 10)
  if (isNaN(n)) return 'empty'
  return n === correctAnswer(p) ? 'correct' : 'wrong'
}

// ─── Single problem card ──────────────────────────────────────────────────────

interface ProblemCardProps {
  problem:   MathProblem
  index:     number
  answer:    string
  checked:   boolean
  layout:    MathLayout
  inputRef:  (el: HTMLInputElement | null) => void
  onAnswer:  (value: string) => void
  onAdvance: () => void   // called on Enter to move to next input
}

function ProblemCard({
  problem,
  index,
  answer,
  checked,
  layout,
  inputRef,
  onAnswer,
  onAdvance,
}: ProblemCardProps) {
  const status = checked ? checkStatus(problem, answer) : 'empty'

  const cardBg =
    status === 'correct' ? '#D1FAE5' :
    status === 'wrong'   ? '#FEE2E2' :
    'white'

  const borderColor =
    status === 'correct' ? '#16A34A' :
    status === 'wrong'   ? '#DC2626' :
    '#FF8B94'   // bubble-pink (Math accent)

  // Parts of the equation
  const { operand1, operand2, operation, result, blankPosition } = problem
  const sym = MATH_OPERATION_SYMBOLS[operation]

  const numStyle: React.CSSProperties = {
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 900,
    fontSize:   layout === 'list' ? '1.3rem' : '1.15rem',
    color:      '#2D2D2D',
    minWidth:   '1.4rem',
    textAlign:  'center',
  }

  const opStyle: React.CSSProperties = {
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 900,
    fontSize:   layout === 'list' ? '1.1rem' : '1rem',
    color:      '#999',
    padding:    '0 2px',
  }

  const inputColor =
    status === 'correct' ? '#16A34A' :
    status === 'wrong'   ? '#DC2626' :
    '#FF6B6B'

  const inputStyle: React.CSSProperties = {
    width:       layout === 'list' ? '3.5rem' : '2.8rem',
    textAlign:   'center',
    border:      `2.5px solid ${inputColor}`,
    borderRadius: 10,
    padding:     '3px 2px',
    fontFamily:  "'Nunito', sans-serif",
    fontWeight:   900,
    fontSize:    layout === 'list' ? '1.3rem' : '1.15rem',
    color:        inputColor,
    background:   'transparent',
    outline:      'none',
    MozAppearance: 'textfield' as React.CSSProperties['MozAppearance'],
  }

  function num(n: number) {
    return <span style={numStyle}>{n}</span>
  }

  function blank() {
    return (
      <input
        ref={inputRef}
        type="number"
        inputMode="numeric"
        min={0}
        value={answer}
        onChange={(e) => onAnswer(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAdvance() } }}
        style={inputStyle}
        aria-label={`Answer for problem ${index + 1}`}
        className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
    )
  }

  const op1El = blankPosition === 'operand1' ? blank() : num(operand1)
  const op2El = blankPosition === 'operand2' ? blank() : num(operand2)
  const resEl = blankPosition === 'result'   ? blank() : num(result)

  return (
    <div
      style={{
        background:   cardBg,
        border:       `2px solid ${borderColor}`,
        borderRadius: 16,
        padding:      layout === 'list' ? '10px 14px' : '10px 8px',
        display:      'flex',
        alignItems:   'center',
        justifyContent: layout === 'grid' ? 'center' : 'flex-start',
        gap:          6,
        transition:   'background 0.15s, border-color 0.15s',
      }}
    >
      {/* Problem number */}
      <span
        style={{
          fontFamily: "'Nunito', sans-serif",
          fontWeight: 900,
          fontSize:   '0.7rem',
          color:      '#BBB',
          minWidth:   '1.4rem',
          flexShrink: 0,
        }}
      >
        {index + 1}.
      </span>

      {/* Equation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {op1El}
        <span style={opStyle}>{sym}</span>
        {op2El}
        <span style={opStyle}>=</span>
        {resEl}
      </div>

      {/* Status icon after checking */}
      {checked && status !== 'empty' && (
        <span style={{ marginLeft: 4, fontSize: '1rem' }}>
          {status === 'correct' ? '✓' : '✗'}
        </span>
      )}
    </div>
  )
}

// ─── Problem set ──────────────────────────────────────────────────────────────

export default function MathProblemSet({
  problems,
  answers,
  checked,
  layout,
  onAnswer,
}: MathProblemSetProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  function advanceTo(idx: number) {
    const next = inputRefs.current[idx]
    if (next) next.focus()
  }

  return (
    <div
      className={
        layout === 'grid'
          ? 'grid grid-cols-2 md:grid-cols-3 gap-2.5'
          : 'flex flex-col gap-2.5'
      }
    >
      {problems.map((p, idx) => (
        <ProblemCard
          key={idx}
          problem={p}
          index={idx}
          answer={answers[idx] ?? ''}
          checked={checked}
          layout={layout}
          inputRef={(el) => { inputRefs.current[idx] = el }}
          onAnswer={(v) => onAnswer(idx, v)}
          onAdvance={() => advanceTo(idx + 1)}
        />
      ))}
    </div>
  )
}
